import React, { useState, useCallback, useRef, useEffect } from 'react';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import { renderAsync } from 'docx-preview';
import { saveAs } from 'file-saver';

// Available built-in templates
const BUILT_IN_TEMPLATES = [
  { id: 'generic', name: 'Generic Letter', file: 'Letter for app generic.docx' },
  { id: 'storm', name: 'Storm Damage', file: 'Letter for app Storm.docx' },
  { id: 'tornado', name: 'Tornado Damage', file: 'Letter for app Tornado.docx' },
  { id: 'shingle', name: 'Shingle Blow Off', file: 'Letter for app shingle blow off.docx' },
  { id: 'metal', name: 'Metal Roof', file: 'Letter for app metal roof.docx' },
  { id: 'commercial', name: 'Commercial', file: 'Letter for app commercial.docx' },
  { id: 'gutter', name: 'Gutter Cleaning', file: 'Letter for app Gutter Cleaning.docx' },
  { id: 'concrete', name: 'Concrete', file: 'Letter for app Concrete.docx' },
  { id: 'repairs', name: 'All Repairs', file: 'Letter for app all repairs.docx' },
  { id: 'discontinued', name: 'Discontinued Shingle', file: 'Discontinued Shingle.docx' },
  { id: 'oldroofs', name: 'Looking for Old Roofs', file: 'Looking for old roofs.docx' },
  { id: 'referral', name: 'Referral Letter', file: 'Referral letter.docx' },
  { id: 'insurance', name: 'Letter to Insurance', file: 'Letter to insurance.docx' },
  { id: 'flyer', name: 'Flyer', file: 'flyer for app.docx' }
];

function DocxFlyerGenerator({ isVisible, onClose, selectedLeads, leads, onFlyerGenerated }) {
  const [selectedTemplate, setSelectedTemplate] = useState('generic');
  const [customTemplate, setCustomTemplate] = useState(null);
  const [customTemplateName, setCustomTemplateName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [editorMode, setEditorMode] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [templateBuffer, setTemplateBuffer] = useState(null);
  const fileInputRef = useRef(null);
  const previewContainerRef = useRef(null);

  // Get selected leads data
  const getSelectedLeadsData = useCallback(() => {
    return leads.filter((_, i) => selectedLeads.has(i));
  }, [leads, selectedLeads]);

  // Load and preview template using docx-preview
  const loadTemplatePreview = useCallback(async (templateId) => {
    setLoadingPreview(true);
    setError(null);

    try {
      let arrayBuffer;

      if (customTemplate) {
        arrayBuffer = customTemplate;
      } else {
        const template = BUILT_IN_TEMPLATES.find(t => t.id === templateId);
        if (!template) throw new Error('Template not found');

        const response = await fetch(`/templates/${template.file}`);
        if (!response.ok) throw new Error(`Failed to load: ${template.file}`);
        arrayBuffer = await response.arrayBuffer();
      }

      setTemplateBuffer(arrayBuffer);

      // Render using docx-preview (renders images/logos correctly)
      if (previewContainerRef.current) {
        previewContainerRef.current.innerHTML = '';
        await renderAsync(arrayBuffer, previewContainerRef.current, null, {
          className: 'docx-preview-content',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true
        });
      }
    } catch (e) {
      console.error('Failed to load template:', e);
      setError(e.message);
    }

    setLoadingPreview(false);
  }, [customTemplate]);

  // Load preview when template changes or editor opens
  useEffect(() => {
    if (editorMode && selectedTemplate) {
      loadTemplatePreview(selectedTemplate);
    }
  }, [editorMode, selectedTemplate, loadTemplatePreview]);

  // Load template file for generation
  const loadTemplate = async () => {
    if (customTemplate) {
      return customTemplate;
    }

    const template = BUILT_IN_TEMPLATES.find(t => t.id === selectedTemplate);
    if (!template) {
      throw new Error('Template not found');
    }

    const response = await fetch(`/templates/${template.file}`);
    if (!response.ok) {
      throw new Error(`Failed to load template: ${template.file}`);
    }
    return await response.arrayBuffer();
  };

  // Get placeholder data for a lead
  const getLeadData = (lead) => {
    const firstName = (lead.ownerName || 'Neighbor').split(' ')[0].split(',')[0].replace(/[^a-zA-Z]/g, '') || 'Neighbor';
    const fullName = lead.ownerName || 'Homeowner';

    return {
      // Standard placeholders (case variations)
      'Insert Name': fullName,
      'insert name': fullName,
      'INSERT NAME': fullName,
      'Insert First Name': firstName,
      'insert first name': firstName,
      'FIRST_NAME': firstName,
      'FULL_NAME': fullName,
      'NAME': fullName,
      'name': fullName,

      // Address
      'ADDRESS': lead.streetAddress || '',
      'address': lead.streetAddress || '',
      'Insert Address': lead.streetAddress || '',
      'STREET': lead.streetAddress || '',
      'CITY': lead.city || '',
      'city': lead.city || '',
      'STATE': lead.state || '',
      'state': lead.state || '',
      'ZIP': lead.zip || '',
      'zip': lead.zip || '',
      'FULL_ADDRESS': `${lead.streetAddress || ''}, ${lead.city || ''}, ${lead.state || ''} ${lead.zip || ''}`.trim(),

      // Property info
      'YEAR_BUILT': lead.yearBuilt || '',
      'year_built': lead.yearBuilt || '',
      'SQFT': lead.sqft ? lead.sqft.toLocaleString() : '',
      'sqft': lead.sqft ? lead.sqft.toLocaleString() : '',
      'ROOF_SQFT': lead.roofSqft ? lead.roofSqft.toLocaleString() : '',
      'ROOF_AGE': lead.roofAge || '',
      'roof_age': lead.roofAge || '',
      'ROOF_TYPE': lead.roofType || '',
      'ROOF_PITCH': lead.roofPitch ? `${lead.roofPitch.toFixed(1)}°` : '',
      'ROOF_SEGMENTS': lead.roofSegments || '',
      'ROOF_MEASURED': lead.roofSqftMeasured ? 'Satellite Measured' : 'Estimated',
      'BEDROOMS': lead.bedrooms || '',
      'BATHROOMS': lead.bathrooms || '',

      // Financial
      'ASSESSED_VALUE': lead.assessedValue ? `$${lead.assessedValue.toLocaleString()}` : '',
      'ROOF_COST_LOW': lead.roofCostAsphalt ? `$${lead.roofCostAsphalt.toLocaleString()}` : '',
      'ROOF_COST_HIGH': lead.roofCostMetal ? `$${lead.roofCostMetal.toLocaleString()}` : '',
      'ROOF_COST': lead.roofCostAsphalt ? `$${lead.roofCostAsphalt.toLocaleString()} - $${(lead.roofCostMetal || lead.roofCostAsphalt * 2).toLocaleString()}` : '',
      'ROOF_COST_ASPHALT': lead.roofCostAsphalt ? `$${lead.roofCostAsphalt.toLocaleString()}` : '',
      'ROOF_COST_METAL': lead.roofCostMetal ? `$${lead.roofCostMetal.toLocaleString()}` : '',
      'ROOF_COST_TILE': lead.roofCostTile ? `$${lead.roofCostTile.toLocaleString()}` : ''
    };
  };

  // Process a single template with lead data
  const processTemplate = async (templateData, lead) => {
    const zip = new PizZip(templateData);
    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: '[', end: ']' }
    });

    const data = getLeadData(lead);
    doc.render(data);

    return doc.getZip().generate({ type: 'blob', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
  };

  // Generate all flyers as separate files
  const generateFlyers = async () => {
    const selectedData = getSelectedLeadsData();
    if (selectedData.length === 0) return;

    setGenerating(true);
    setProgress(0);
    setError(null);

    try {
      const templateData = await loadTemplate();

      for (let i = 0; i < selectedData.length; i++) {
        const lead = selectedData[i];

        try {
          const blob = await processTemplate(templateData, lead);
          const ownerName = (lead.ownerName || 'Unknown').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
          const address = (lead.streetAddress || '').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
          const filename = `letter_${ownerName}_${address}.docx`;
          saveAs(blob, filename);
        } catch (err) {
          console.error(`Failed to process lead ${i}:`, err);
        }

        setProgress(Math.round(((i + 1) / selectedData.length) * 100));

        if (i < selectedData.length - 1) {
          await new Promise(r => setTimeout(r, 300));
        }
      }
      // Reward user for generating flyers
      if (onFlyerGenerated && selectedData.length > 0) {
        onFlyerGenerated(selectedData.length);
      }
    } catch (e) {
      console.error('Failed to generate DOCX:', e);
      setError(e.message);
    }

    setGenerating(false);
    setProgress(0);
  };

  // Handle custom template upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.docx')) {
      setError('Please upload a .docx file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      setCustomTemplate(event.target.result);
      setCustomTemplateName(file.name);
      setSelectedTemplate('custom');
      setError(null);
      setTemplateBuffer(event.target.result);

      // Auto-load preview if in editor mode
      if (editorMode && previewContainerRef.current) {
        try {
          previewContainerRef.current.innerHTML = '';
          await renderAsync(event.target.result, previewContainerRef.current, null, {
            className: 'docx-preview-content',
            inWrapper: true,
            useBase64URL: true
          });
        } catch (err) {
          setError(err.message);
        }
      }
    };
    reader.onerror = () => setError('Failed to read file');
    reader.readAsArrayBuffer(file);
  };

  // Clear custom template
  const clearCustomTemplate = () => {
    setCustomTemplate(null);
    setCustomTemplateName('');
    setSelectedTemplate('generic');
    setTemplateBuffer(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (previewContainerRef.current) previewContainerRef.current.innerHTML = '';
  };

  if (!isVisible) return null;

  const selectedCount = selectedLeads.size;

  return (
    <div className="flyer-overlay">
      <div className={`flyer-modal docx-modal ${editorMode ? 'editor-expanded' : ''}`}>
        <button className="flyer-close" onClick={onClose}>&times;</button>

        <div className="modal-header">
          <h2>Generate DOCX Letters</h2>
          <div className="mode-toggle">
            <button
              className={`mode-btn ${!editorMode ? 'active' : ''}`}
              onClick={() => setEditorMode(false)}
            >
              Templates
            </button>
            <button
              className={`mode-btn ${editorMode ? 'active' : ''}`}
              onClick={() => setEditorMode(true)}
            >
              Editor
            </button>
          </div>
        </div>

        {!editorMode ? (
          <>
            {/* Template Selection Mode */}
            <p className="flyer-subtitle">Select a template and generate personalized letters</p>

            <div className="company-section">
              <h3>Select Template</h3>
              <div className="template-grid">
                {BUILT_IN_TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    className={`template-btn ${selectedTemplate === template.id && !customTemplate ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedTemplate(template.id);
                      setCustomTemplate(null);
                      setCustomTemplateName('');
                    }}
                  >
                    {template.name}
                  </button>
                ))}
              </div>

              <div className="custom-upload-section">
                <h4>Or Upload Custom Template</h4>
                <div className="upload-row">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".docx"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <button className="upload-btn" onClick={() => fileInputRef.current?.click()}>
                    Upload DOCX
                  </button>
                  {customTemplateName && (
                    <div className="custom-template-info">
                      <span>{customTemplateName}</span>
                      <button className="clear-btn" onClick={clearCustomTemplate}>x</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Placeholder Reference */}
            <div className="placeholder-reference">
              <details>
                <summary>Available Placeholders</summary>
                <div className="placeholder-list">
                  <code>[Insert Name]</code> <code>[Insert First Name]</code>
                  <code>[ADDRESS]</code> <code>[CITY]</code> <code>[STATE]</code> <code>[ZIP]</code>
                  <code>[YEAR_BUILT]</code> <code>[SQFT]</code> <code>[ROOF_SQFT]</code>
                  <code>[ROOF_AGE]</code> <code>[ROOF_TYPE]</code> <code>[ROOF_PITCH]</code>
                  <code>[ROOF_MEASURED]</code> <code>[ASSESSED_VALUE]</code>
                  <code>[ROOF_COST]</code> <code>[ROOF_COST_ASPHALT]</code>
                  <code>[ROOF_COST_METAL]</code> <code>[ROOF_COST_TILE]</code>
                </div>
              </details>
            </div>
          </>
        ) : (
          <>
            {/* Editor Mode - Full DOCX Preview */}
            <p className="flyer-subtitle">Preview template with images and formatting</p>

            <div className="editor-toolbar">
              <select
                value={selectedTemplate}
                onChange={(e) => {
                  setSelectedTemplate(e.target.value);
                  setCustomTemplate(null);
                }}
              >
                {BUILT_IN_TEMPLATES.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>

              <button className="upload-btn small" onClick={() => fileInputRef.current?.click()}>
                Upload Custom
              </button>

              {customTemplateName && (
                <span className="custom-name">{customTemplateName}</span>
              )}
            </div>

            <div className="docx-preview-container">
              {loadingPreview ? (
                <div className="editor-loading">Loading template preview...</div>
              ) : (
                <div ref={previewContainerRef} className="docx-preview-wrapper" />
              )}
            </div>

            <div className="placeholder-info">
              <h4>Template Placeholders</h4>
              <p>Your template can use these placeholders (in brackets):</p>
              <div className="placeholder-list">
                <code>[Insert Name]</code> <code>[Insert First Name]</code>
                <code>[ADDRESS]</code> <code>[CITY]</code> <code>[STATE]</code> <code>[ZIP]</code>
                <code>[YEAR_BUILT]</code> <code>[SQFT]</code> <code>[ROOF_SQFT]</code>
                <code>[ROOF_AGE]</code> <code>[ROOF_PITCH]</code> <code>[ROOF_MEASURED]</code>
                <code>[ASSESSED_VALUE]</code> <code>[ROOF_COST]</code>
                <code>[ROOF_COST_ASPHALT]</code> <code>[ROOF_COST_METAL]</code>
              </div>
            </div>
          </>
        )}

        {error && <div className="error-message">{error}</div>}

        {/* Selected Leads */}
        <div className="selected-section">
          <h3>Selected Leads: {selectedCount}</h3>
          {selectedCount === 0 ? (
            <p className="no-leads">No leads selected. Go back and select some addresses.</p>
          ) : (
            <div className="leads-preview">
              {getSelectedLeadsData().slice(0, 3).map((lead, i) => (
                <div key={i} className="lead-preview-item">
                  <strong>{lead.ownerName || 'Homeowner'}</strong>
                  <span>{lead.streetAddress}</span>
                </div>
              ))}
              {selectedCount > 3 && <div className="more-leads">+ {selectedCount - 3} more...</div>}
            </div>
          )}
        </div>

        {/* Generate Button */}
        <div className="generate-section">
          {generating ? (
            <div className="progress-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span>Generating... {progress}%</span>
            </div>
          ) : (
            <button
              className="generate-btn"
              onClick={generateFlyers}
              disabled={selectedCount === 0}
            >
              Generate {selectedCount} Letter{selectedCount !== 1 ? 's' : ''} (DOCX)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocxFlyerGenerator;
